import "../App.css";
import { useEffect, useState, useRef } from "react";
import { Box, Checkbox, Typography, useMediaQuery } from "@mui/material";
import jsonData from "../assets/traits.json";
import BaseCarousel from "../components/BaseCarousel";
import ColorCarousel from "../components/ColorCarousel";
import TypeCarousel from "../components/TypeCarousel";
import TestCarousel from "../components/TestCarousel";
import BaseIcon from "../assets/images/icons/1.svg";
import SkinColorIcon from "../assets/images/icons/2.svg";
import EyeIcon from "../assets/images/icons/3.svg";
import HairIcon from "../assets/images/icons/4.svg";
import HairColorIcon from "../assets/images/icons/5.svg";
import OutfitIcon from "../assets/images/icons/6.svg";
import FacialHairIcon from "../assets/images/icons/7.svg";
import FacialHairColorIcon from "../assets/images/icons/8.svg";
import GlassesIcon from "../assets/images/icons/9.svg";
import CardLoader from "../assets/card-loader.gif";
import CharacterCustom from "../components/CharacterCustom";
import { BOOK_BUILDER } from "../constants/common";

const CharacterCustomizer = ({ gender, data, label, action, pageNumber, imagesLoaded, setImagesLoaded, description }) => {
  const myRef = useRef(null);

  const [isMale, setSex] = useState("male");
  const [baseList, setBase] = useState([]);
  const [eyesList, setEyes] = useState([]);
  const [hairList, setHairs] = useState([]);
  const [outfitList, setOutfitList] = useState([]);
  const [glassesList, setGlassesList] = useState([]);
  const [facialHairList, setFacialHairList] = useState([]);

  const [base, setBaseData] = useState();
  const [eyes, setEyesData] = useState();
  const [hair, setHairData] = useState();
  const [outfit, setOutfitData] = useState();
  const [glasses, setGlassesData] = useState();
  const [facialHair, setFacialHairData] = useState();
  const [value, setInputValue] = useState("");
  const [originalValue, setOriginalValue] = useState("");
  const [isAutoCapitalization, setAutoCapitalization] = useState(true);
  const [currentPage, setCurrentPage] = useState("base");

  const isSmallScreen = useMediaQuery("(max-width: 660px)");
  const destination = sessionStorage.getItem("destination");
  const loadedAllImages = !imagesLoaded.base && !imagesLoaded.eyes && !imagesLoaded.hair && !imagesLoaded.outfit;

  const handleImageLoad = (type) => {
    setImagesLoaded((prevState) => ({ ...prevState, [type]: true }));
  };

  const baseData = jsonData[0];

  useEffect(() => {
    if (!data.base) {
      setSex(gender);
      setDefaultData(gender);
      generateJsonData(gender);
      setInputValue("");
      setOriginalValue("");
    } else {
      const { sex } = data;
      if (sex === "female") {
        setSex("female");
        generateJsonData("female");
      } else {
        setSex("male");
        generateJsonData("male");
      }
      setBaseData(data.base);
      setEyesData(data.eyes);
      setHairData(data.hair);
      setFacialHairData(data.facialHair);
      setGlassesData(data.glasses);
      setOutfitData(data.outfit);
    }
    setInputValue(data.name || "");
    setOriginalValue(data.name || "");
    setCurrentPage("base");
  }, [label]);

  const generateJsonData = (sex) => {
    const base = baseData.contents.find((x) => x.name === "base").contents.find((x) => x.name === sex).contents;
    setBase(base);
    const eyes = baseData.contents.find((x) => x.name === "eyes").contents.find((x) => x.name === sex).contents;
    setEyes(eyes);
    const hair = baseData.contents.find((x) => x.name === "hair").contents.find((x) => x.name === sex).contents;
    setHairs(hair);
    const outfit = baseData.contents.find((x) => x.name === "outfit").contents.find((x) => x.name === sex).contents;
    setOutfitList(outfit);
    const glasses = baseData.contents.find((x) => x.name === "glasses").contents.find((x) => x.name === sex).contents;
    setGlassesList(glasses);
    const facialHair = baseData.contents.find((x) => x.name === "facial_hair").contents.find((x) => x.name === "male").contents;
    setFacialHairList(facialHair);
    setDefaultData(sex);
  };

  const getData = () => {
    action({
      base,
      hair,
      outfit,
      eyes,
      facialHair,
      glasses,
      name: value,
      sex: isMale,
    });
  };

  const resetImagesLoaded = () => {
    setImagesLoaded({
      base: false,
      eyes: false,
      hair: false,
      outfit: false,
    });
  };

  const onChangeSex = (sex) => {
    if (isMale !== sex) {
      resetImagesLoaded();
      setSex(sex);
      generateJsonData(sex);
    }
    getData();
  };

  const setComponent = (type, value) => {
    switch (type) {
      case "base":
        setBaseData(`${isMale}/${value}`);
        break;
      case "hair":
        setHairData(`${isMale}/${value}`);
        break;
      case "eyes":
        setEyesData(`${isMale}/${value}`);
        break;
      case "outfit":
        setOutfitData(`${isMale}/${value}`);
        break;
      case "glasses":
        setGlassesData(`${isMale}/${value}`);
        break;
      case "facial_hair":
        setFacialHairData(`${isMale}/${value}`);
        break;
      default:
        break;
    }
    scrollToRef();
  };

  const transformValue = (value) => {
    const intermediate = value.replace(/([.!?]\s)?([A-Z])/g, (match, p1, p2) => {
      return p1 ? p1 + p2 : p2.toLowerCase();
    });

    return intermediate.charAt(0).toUpperCase() + intermediate.slice(1);
  };

  const onChange = (event) => {
    setInputValue(event.target.value);
    if (isAutoCapitalization) {
      setOriginalValue(event.target.value);
      setInputValue(transformValue(event.target.value));
    }
  };

  const handleCheckCapitalization = (event) => {
    setAutoCapitalization(event.target.checked);
    if (event.target.checked) {
      setOriginalValue(value);
      setInputValue(value.toLowerCase().replace(/\b\w/g, (m) => m.toUpperCase()));
    } else {
      setInputValue(originalValue);
    }
  };

  useEffect(() => {
    getData();
  }, [base, eyes, hair, outfit, value]);

  const scrollToRef = () => {
    myRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const setDefaultData = (sex) => {
    if (sex === "male") {
      setOutfitData(`${sex}/om01c01.png`);
      setEyesData(`${sex}/em01c01.png`);
      setBaseData(`${sex}/bm01c01.png`);
      setHairData(`${sex}/hm01c01.png`);
      setFacialHairData(`${sex}/fm01c01.png`);
      setGlassesData(`${sex}/gm01c01.png`);
    } else {
      setOutfitData(`${sex}/of01c01.png`);
      setEyesData(`${sex}/ef01c01.png`);
      setBaseData(`${sex}/bf01c01.png`);
      setHairData(`${sex}/hf01c01.png`);
      setGlassesData(`${sex}/gf01c01.png`);
    }
  };

  const setPage = () => {
    const hair = baseData.contents.find((x) => x.name === "hair").contents.find((x) => x.name === isMale).contents;
    setHairs(hair);
    setCurrentPage("hair");
  };

  const getCategoryName = (name) => {
    switch (name) {
      case "base":
        return "Base";
      case "skinColor":
        return "Skin Color";
      case "eyes":
        return "Eyes";
      case "hair":
        return "Hair";
      case "hairColor":
        return "Hair Color";
      case "outfit":
        return "Outfit";
      case "facial_hair":
        return "Facial Hair";
      case "facialHairColor":
        return "Facial Hair Color";
      case "glasses":
        return "Glasses";
      default:
        return "";
    }
  };

  return (
    <Box>
      <Box className="character-creator-data">
        <p className="character-creator-page">{pageNumber}</p>
        <p className="character-creator-label_main">
          Personalize <span>{label}</span>
        </p>
      </Box>
      <p className="character-creator-label">{description}</p>
      <Box className="character-container" ref={myRef}>
        <Box className="character-preview-container">
          <Box className="background_сharacter" />
          <Box className="characterPage">
            {loadedAllImages && (
              <Box sx={{ height: isSmallScreen ? "446px" : "502px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                <img src={CardLoader} style={{ width: "40%", marginTop: isSmallScreen ? "0px" : "14px" }} />
              </Box>
            )}
            <Box
              sx={{
                display: loadedAllImages ? "none" : "flex",
              }}
            >
              <CharacterCustom
                isMale={isMale}
                base={base}
                hair={hair}
                eyes={eyes}
                outfit={outfit}
                facialHair={facialHair}
                glasses={glasses}
                handleImageLoad={handleImageLoad}
              />
            </Box>
          </Box>
        </Box>
        <Box className="components_wrapper">
          <Box className="components-container">
            <Box className="component-radius-container" sx={{ cursor: "pointer" }}>
              <img
                alt="BaseIcon"
                src={BaseIcon}
                className={`component-radius ${currentPage === "base" ? "component-radius-fill" : ""}`}
                onClick={(e) => setCurrentPage("base")}
              />
            </Box>
            <Box className="component-radius-container" sx={{ cursor: "pointer" }}>
              <img
                alt="SkinColorIcon"
                src={SkinColorIcon}
                className={`component-radius ${currentPage === "skinColor" ? "component-radius-fill" : ""}`}
                onClick={(e) => setCurrentPage("skinColor")}
              />
            </Box>
            <Box className="component-radius-container" sx={{ cursor: "pointer" }}>
              <img
                alt="EyeIcon"
                src={EyeIcon}
                className={`component-radius ${currentPage === "eyes" ? "component-radius-fill" : ""}`}
                onClick={(e) => setCurrentPage("eyes")}
              />
            </Box>
            <Box className="component-radius-container" sx={{ cursor: "pointer" }}>
              <img
                alt="HairIcon"
                src={HairIcon}
                className={`component-radius ${currentPage === "hair" ? "component-radius-fill" : ""}`}
                onClick={(e) => setPage()}
              />
            </Box>
            <Box className="component-radius-container" sx={{ cursor: "pointer" }}>
              <img
                alt="HairColorIcon"
                src={HairColorIcon}
                className={`component-radius ${currentPage === "hairColor" ? "component-radius-fill" : ""}`}
                onClick={(e) => setCurrentPage("hairColor")}
              />
            </Box>
            <Box className="component-radius-container" sx={{ cursor: "pointer" }}>
              <img
                alt="OutfitIcon"
                src={OutfitIcon}
                className={`component-radius ${currentPage === "outfit" ? "component-radius-fill" : ""}`}
                onClick={(e) => setCurrentPage("outfit")}
              />
            </Box>
            {/* {isMale === "male" && (
              <>
                <Box className="component-radius-container" sx={{ cursor: "pointer" }}>
                  <img
                    alt="FacialHairIcon"
                    src={FacialHairIcon}
                    className={`component-radius ${currentPage === "facial_hair" ? "component-radius-fill" : ""}`}
                    onClick={(e) => setCurrentPage("facial_hair")}
                  />
                </Box>
                <Box className="component-radius-container" sx={{ cursor: "pointer" }}>
                  <img
                    alt="FacialHairColorIcon"
                    src={FacialHairColorIcon}
                    className={`component-radius ${currentPage === "facialHairColor" ? "component-radius-fill" : ""}`}
                    onClick={(e) => setCurrentPage("facialHairColor")}
                  />
                </Box>
              </>
            )}
            <Box className="component-radius-container" sx={{ cursor: "pointer" }}>
              <img
                alt="GlassesIcon"
                src={GlassesIcon}
                className={`component-radius ${currentPage === "glasses" ? "component-radius-fill" : ""}`}
                onClick={(e) => setCurrentPage("glasses")}
              />
            </Box> */}
          </Box>
          <Box className="choose_character_skin">
            Choose the <span>{getCategoryName(currentPage)}</span>
          </Box>
          {(() => {
            switch (currentPage) {
              case "skinColor":
                return <TestCarousel action={setComponent} base={base} data={baseList} type="base" sex={isMale} />;
              case "eyes":
                return <TestCarousel action={setComponent} eyes={eyes} data={eyesList} type="eyes" sex={isMale} />;
              case "hair":
                return <TypeCarousel action={setComponent} hair={hair} data={hairList} type="hair" sex={isMale} />;
              // case "facial_hair":
              //   return <TypeCarousel action={setComponent} hair={facialHair} data={facialHairList} type="facial_hair" sex={isMale} />;
              case "outfit":
                return <TestCarousel action={setComponent} outfit={outfit} data={outfitList} type="outfit" sex={isMale} />;
              case "base":
                return <BaseCarousel isMale={isMale} onChangeSex={onChangeSex} />;
              case "hairColor":
                return <ColorCarousel action={setComponent} variant={hair} data={hairList} sex={isMale} type="hair" />;
              // case "facialHairColor":
              //   return <ColorCarousel action={setComponent} variant={facialHair} data={facialHairList} sex={isMale} type="facial_hair" />;
              // case "glasses":
              //   return <TypeCarousel action={setComponent} hair={glasses} data={glassesList} type="glasses" sex={isMale} />;
              default:
                return null;
            }
          })()}
        </Box>
      </Box>
      {destination === BOOK_BUILDER && (
        <Box className="character-name-container">
          <p className="character-name-label">{pageNumber === "01" ? "What’s your partner’s name?" : "What’s your name?"}</p>
          <input className="character-name-input" placeholder="John Doe" value={value} onChange={onChange} />
          <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
            <Checkbox
              checked={isAutoCapitalization}
              onChange={handleCheckCapitalization}
              sx={{
                "&.Mui-checked": {
                  color: "#FFFFFF",
                },
                "&.MuiCheckbox-root": {
                  color: "#FFFFFF",
                },
              }}
            />
            <Typography sx={{ color: "#FFFFFF", fontFamily: "Manrope", fontWeight: 600 }}>Correct Capitalization Automatically</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CharacterCustomizer;
